import { Component, Input, Output, EventEmitter, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ThesaurusSearchType } from 'src/app/models/search-field-dto';
import { OverlayPanel } from 'primeng/overlaypanel';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Generic search type selector component for choosing between branch, mid, or wid search modes
 */
@Component({
  selector: 'app-search-type-selector',
  templateUrl: './search-type-selector.component.html',
  styleUrls: ['./search-type-selector.component.scss']
})
export class SearchTypeSelectorComponent implements OnInit, OnDestroy {
  @Input() value: ThesaurusSearchType = 'branch';
  @Input() uniqueId: string = 'default';
  @Output() valueChange = new EventEmitter<ThesaurusSearchType>();
  
  @ViewChild('overlay') overlay!: OverlayPanel;
  
  currentValue: ThesaurusSearchType = 'branch';
  currentLanguage: string = 'he';
  private openerElement: HTMLElement | null = null;

  searchTypeOptions: Array<{ label: string; value: ThesaurusSearchType; icon: string }> = [];
  
  private languageSubscription?: Subscription;

  constructor(
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    // Use onLangChange so translateService.instant() runs after new translations are loaded
    this.updateLabels();
    this.languageSubscription = this.translateService.onLangChange.subscribe((event) => {
      this.currentLanguage = event.lang;
      this.updateLabels();
    });
  }

  ngOnDestroy() {
    this.languageSubscription?.unsubscribe();
  }

  private updateLabels() {
    this.searchTypeOptions = [
      { 
        label: this.translateService.instant('iaa.app.advancedSearch.searchTypeBranch'),
        value: 'branch' as ThesaurusSearchType, 
        icon: 'assets/branch.svg' 
      },
      { 
        label: this.translateService.instant('iaa.app.advancedSearch.searchTypeMid'),
        value: 'term' as ThesaurusSearchType, 
        icon: 'assets/mid.svg' 
      },
      { 
        label: this.translateService.instant('iaa.app.advancedSearch.searchTypeWid'),
        value: 'word' as ThesaurusSearchType, 
        icon: 'assets/wid.svg' 
      }
    ];
  }

  toggle(event: Event): void {
    // Store the opener element for focus restoration if not already set
    // (allow parent components to set it explicitly before calling toggle)
    if (!this.openerElement) {
      this.openerElement = (event.target as HTMLElement) || (event.currentTarget as HTMLElement);
    }
    // Sync the local value with the input value when opening
    this.currentValue = this.value;
    this.overlay.toggle(event);
  }

  onOverlayHide(): void {
    // Restore focus to the opener element when overlay closes
    setTimeout(() => {
      if (this.openerElement) {
        this.openerElement.focus();
        this.openerElement = null;
      }
    }, 200);
  }

  onOverlayShow(): void {
    // Focus the currently selected radio button when overlay opens for keyboard accessibility
    setTimeout(() => {
      const selectedRadio = document.querySelector(`#searchType-${this.uniqueId}-${this.currentValue}`) as HTMLElement;
      if (selectedRadio) {
        selectedRadio.focus();
      }
    }, 50);
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.overlay.hide();
      return;
    }
    
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.navigateOptions(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
  }

  navigateOptions(direction: number): void {
    const currentIndex = this.searchTypeOptions.findIndex(opt => opt.value === this.currentValue);
    const nextIndex = (currentIndex + direction + this.searchTypeOptions.length) % this.searchTypeOptions.length;
    this.currentValue = this.searchTypeOptions[nextIndex].value;
    
    // Focus the next radio button
    setTimeout(() => {
      const nextRadio = document.querySelector(`#searchType-${this.uniqueId}-${this.currentValue}`) as HTMLElement;
      if (nextRadio) {
        nextRadio.focus();
      }
    }, 0);
  }

  onRadioClick(): void {
    // Radio button click confirms selection
    setTimeout(() => this.confirmSelection(), 0);
  }

  onLabelClick(): void {
    // Label click confirms selection
    setTimeout(() => this.confirmSelection(), 0);
  }

  confirmSelection(): void {
    this.valueChange.emit(this.currentValue);
    this.overlay.hide();
  }

  hide(): void {
    this.overlay.hide();
  }
}
